import { Link, useNavigate, useLocation } from "react-router-dom";
import "../navbar/nav.css";
import SignIn from "../login/SignIn";
import SignUp from "../login/SignUp";
import { useDispatch, useSelector } from "react-redux";
import { logout } from "../../store/reducers";
import { toast } from "react-toastify";
import LogoutIcon from "@mui/icons-material/Logout";
import { useEffect, useRef, useState } from "react";
import { decryptAndRetrieveData } from "../login/encript-decript";
import { useCookies } from "react-cookie";
import Sidebar from "../Sidebar";
import MenuButton from "../MenuButton";
import axios from "axios";

function Navbar() {
  const token = localStorage.getItem("accessToken") || "";
  const user = decryptAndRetrieveData("isAuth", "123456789");
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();

  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [isNavOpen, setIsNavOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 991);
  const [loading, setLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [isBulkImportOpen, setIsBulkImportOpen] = useState(false);

  const dropdownRef = useRef(null);
  const bulkImportRef = useRef(null);

  // Check if user is admin - checks both localStorage and decoded token
  useEffect(() => {
    try {
      const userRole = localStorage.getItem("userRole") || "";
      const userPermissions = localStorage.getItem("userPermissions") || "";
      
      // Also check token for user type
      let tokenUserType = "";
      if (token) {
        try {
          const decodedToken = JSON.parse(atob(token.split(".")[1]));
          tokenUserType = decodedToken["custom:user_type"] || "";
        } catch (e) {
          console.error("Error decoding token:", e);
        }
      }
      
      const isAdminUser = 
        userRole?.toLowerCase() === "admin" || 
        userPermissions?.toLowerCase().includes("admin") ||
        tokenUserType?.toLowerCase() === "admin";
      
      // Debug: Print user details to console
      console.log("=== NAVBAR USER DETAILS ===");
      console.log("User Auth:", user);
      console.log("User Role (localStorage):", userRole);
      console.log("User Permissions (localStorage):", userPermissions);
      console.log("User Type (from token):", tokenUserType);
      console.log("Is Admin:", isAdminUser);
      console.log("Token:", token ? "✓ Present" : "✗ Not Present");
      console.log("============================");
      
      setIsAdmin(isAdminUser);
    } catch (error) {
      console.error("Error checking admin status:", error);
      setIsAdmin(false);
    }
  }, [token]);

  const handleClickOutside = (event) => {
    if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
      setIsLoggedIn(false);
    }
    if (bulkImportRef.current && !bulkImportRef.current.contains(event.target)) {
      setIsBulkImportOpen(false);
    }
  };

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleNavToggle = () => {
    setIsNavOpen((prev) => !prev);
  };
  const handleLinkClick = () => {
    setIsNavOpen(false);
  };

  const apiBase = (process.env.REACT_APP_API_URL || "").replace(/\/$/, "");
  const buildUrl = (path) => (apiBase ? `${apiBase}${path}` : path);

  const axiosConfig = {
    headers: token
      ? {
          Authorization: `Bearer ${token}`,
        }
      : {},
  };

  const fileInputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatus, setUploadStatus] = useState("idle");
  const [uploadMessage, setUploadMessage] = useState("");
  const [failedRows, setFailedRows] = useState([]);

  const handleDownloadTemplate = async () => {
    // Admin check
    if (!isAdmin) {
      toast.error("You do not have permission to download templates.");
      return;
    }

    const relativeUrl = "/template/download";
    const absoluteUrl = apiBase ? `${apiBase}${relativeUrl}` : null;

    const tryFetch = async (url) => {
      try {
        const resp = await axios.get(url, {
          ...axiosConfig,
          responseType: "arraybuffer",
          validateStatus: (s) => true,
        });
        const status = resp.status;
        const headers = resp.headers || {};
        const text = new TextDecoder("utf-8").decode(
          resp.data || new ArrayBuffer(0)
        );
        let json = null;
        try {
          json = JSON.parse(text);
        } catch {
          json = null;
        }
        return {
          ok: status >= 200 && status < 300,
          status,
          headers,
          text,
          json,
        };
      } catch (err) {
        return { ok: false, error: err };
      }
    };

    try {
      setLoading(true);

      let result = null;
      if (absoluteUrl) {
        result = await tryFetch(absoluteUrl);
        if (result && result.error) {
          console.warn(
            `Absolute URL failed (${absoluteUrl}). Falling back to relative path.`,
            result.error
          );
          result = await tryFetch(relativeUrl);
        }
      } else {
        result = await tryFetch(relativeUrl);
      }

      if (!result) {
        toast.error("No response when trying to download template.");
        return;
      }

      if (!result.ok) {
        const message =
          (result.json && (result.json.message || result.json.error)) ||
          result.text ||
          `HTTP ${result.status}`;
        toast.error("Download failed: " + message);
        console.error("Download attempt failed:", result);
        return;
      }

      if (result.json) {
        const possible =
          result.json.url ||
          result.json.downloadUrl ||
          result.json.signedUrl ||
          result.json.data?.url;
        if (possible) {
          window.open(possible, "_blank");
          toast.success("Opening download link...");
          setIsBulkImportOpen(false);
          return;
        }
      }

      const text = (result.text || "").trim();
      if (text.startsWith("http")) {
        window.open(text, "_blank");
        toast.success("Opening download link...");
        setIsBulkImportOpen(false);
        return;
      }

      const urlRegex = /(https?:\/\/[^\s"'<]+)/i;
      const match = text.match(urlRegex);
      if (match) {
        window.open(match[0], "_blank");
        toast.success(result.json?.message || "Download started successfully");
        setIsBulkImportOpen(false);
        return;
      }

      toast.error(
        "Download succeeded but no valid URL found in response. See console."
      );
      console.warn("Unhandled download response:", result);
    } catch (err) {
      console.error("handleDownloadTemplate error:", err);
      toast.error("Failed to download template. See console for details.");
    } finally {
      setLoading(false);
    }
  };

  const handleUploadClick = () => {
    // Admin check
    if (!isAdmin) {
      toast.error("You do not have permission to upload files.");
      return;
    }
    fileInputRef.current?.click();
  };

  const handleFileChange = async (e) => {
    // Admin check
    if (!isAdmin) {
      toast.error("You do not have permission to upload files.");
      return;
    }

    const file = e?.target?.files?.[0];
    console.log("Selected file:", file);
    if (!file) {
      toast.warn("No file selected.");
      return;
    }

    const isValidExcel =
      file.name.endsWith(".xlsx") ||
      file.type ===
        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

    if (!isValidExcel) {
      toast.error("Please upload an .xlsx file only.");
      if (fileInputRef.current) fileInputRef.current.value = "";
      return;
    }

    setUploading(true);
    setUploadProgress(0);
    setUploadStatus("uploading");
    setUploadMessage("");
    setFailedRows([]);

    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_name", file.name);

      const uploadUrl = buildUrl("/template/upload");
      const uploadRes = await fetch(uploadUrl, {
        method: "POST",
        body: formData,
        headers: {
          Authorization: token ? `Bearer ${token}` : "",
        },
      });

      const uploadText = await uploadRes.text().catch(() => "");
      if (!uploadRes.ok) {
        throw new Error(
          `Upload endpoint failed: ${uploadRes.status} ${uploadText}`
        );
      }

      let uploadData = {};
      try {
        uploadData = uploadText ? JSON.parse(uploadText) : {};
      } catch (err) {
        throw new Error("Upload endpoint returned invalid JSON.");
      }

      let s3Key = uploadData.key || uploadData.s3_key;
      const presignedPostUrl = uploadData.url;
      if (presignedPostUrl) {
        const postForm = new FormData();

        if (uploadData.fields) {
          Object.entries(uploadData.fields).forEach(([key, value]) => {
            postForm.append(key, value);
          });
          if (!s3Key && uploadData.fields.key) {
            s3Key = uploadData.fields.key;
          }
        }
        postForm.append("file", file);
        const res = await fetch(presignedPostUrl, {
          method: "POST",
          body: postForm,
          credentials: "omit",
        });

        if (!res.ok) {
          const txt = await res.text().catch(() => "(no body)");
          throw new Error(`S3 upload failed: ${res.status} ${txt}`);
        }

        console.log("✅ File uploaded successfully to S3");
      } else if (s3Key) {
        // Continue with s3Key
      } else {
        throw new Error(
          "Upload endpoint returned neither s3_key nor a presigned POST URL."
        );
      }

      if (!s3Key) {
        throw new Error("No s3_key returned from upload endpoint.");
      }

      const payload = { s3_key: s3Key };
      console.log("Calling /company/add/bulk with:", payload);

      const bulkRes = await axios.post(buildUrl("/company/add/bulk"), payload, {
        ...axiosConfig,
        validateStatus: (s) => true,
      });

      if (!(bulkRes.status >= 200 && bulkRes.status < 300)) {
        let errorMsg = "Processing failed";
        if (bulkRes.data?.body) {
          errorMsg =
            typeof bulkRes.data.body === "string"
              ? bulkRes.data.body
              : JSON.stringify(bulkRes.data.body);
        } else if (bulkRes.data?.message) {
          errorMsg = bulkRes.data.message;
        }
        throw new Error(errorMsg);
      }

      let responseData = bulkRes.data || {};
      if (responseData.statusCode && responseData.body) {
        try {
          responseData =
            typeof responseData.body === "string"
              ? JSON.parse(responseData.body)
              : responseData.body;
        } catch (_) {}
      }

      setUploadStatus("success");
      setUploadProgress(100);
      setUploadMessage(
        responseData.message ||
          `Successfully processed ${responseData.successful || 0} companies`
      );

      if (responseData.failed_rows && responseData.failed_rows.length > 0) {
        setFailedRows(responseData.failed_rows);
        toast.warn(
          `${responseData.successful || 0} succeeded, ${
            responseData.failed || 0
          } failed`
        );
      } else {
        toast.success("Bulk upload has been queued for processing. You will receive an email notification upon completion.");
      }

      window.dispatchEvent(
        new CustomEvent("data:updated", { detail: { source: "bulk-upload" } })
      );

      if (fileInputRef.current) fileInputRef.current.value = "";
      setIsBulkImportOpen(false);
    } catch (error) {
      console.error("Upload error:", error);
      setUploadStatus("error");
      setUploadMessage(error.message || "Upload failed");
      toast.error(error.message || "Upload failed");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const logoutComponent = () => {
    dispatch(logout());
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    localStorage.removeItem("isAuth");
    localStorage.removeItem("userName");
    localStorage.removeItem("loginTime");
    localStorage.removeItem("path");
    localStorage.removeItem("userRole");
    localStorage.removeItem("userPermissions");
    toast.success("Logout successfully!");
    navigate("/");
    setIsNavOpen(false);
  };

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 991);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const loginuser = decryptAndRetrieveData("user", "123456789");
  const dummyImage =
    "https://www.kindpng.com/picc/m/22-223965_no-profile-picture-icon-circle-member-icon-png.png";

  return (
    <>
      <style>
        {`
          .navbar-clean {
            background-color: #ffffff;
            padding: 0.75rem 1rem;
            box-shadow: 0 1px 3px rgba(0, 0, 0, 0.08);
          }

          .navbar-clean .container-fluid {
            display: flex;
            align-items: center;
            gap: 1rem;
          }

          .logo-container {
            display: flex;
            align-items: center;
            margin: 0 1rem;
          }

          .logo-container img {
            height: 45px;
            width: auto;
          }

          .nav-center-items {
            display: flex;
            align-items: center;
            gap: 2rem;
            flex: 1;
            margin: 0 1rem;
          }

          .nav-link-clean {
            font-size: 15px;
            font-weight: 400;
            color: #333333;
            text-decoration: none;
            padding: 0;
            transition: color 0.2s ease;
            cursor: pointer;
            border: none;
            background: none;
            white-space: nowrap;
          }

          .nav-link-clean:hover {
            color: #555555;
          }

          .nav-link-clean.active {
            color: #333333;
            border-bottom: 1px solid #333333;
            padding-bottom: 2px;
          }

          .nav-link-clean:disabled {
            opacity: 0.6;
            cursor: not-allowed;
          }

          .nav-end-section {
            display: flex;
            align-items: center;
            gap: 1.5rem;
            margin-left: auto;
          }

          .profile-button {
            width: 40px;
            height: 40px;
            border-radius: 50%;
            border: 2px solid #e0e0e0;
            background: none;
            cursor: pointer;
            display: flex;
            align-items: center;
            justify-content: center;
            overflow: hidden;
            transition: border-color 0.2s ease;
            padding: 0;
          }

          .profile-button:hover {
            border-color: #333333;
          }

          .profile-button img {
            width: 100%;
            height: 100%;
            object-fit: cover;
          }

          .profile-initial {
            width: 100%;
            height: 100%;
            display: flex;
            align-items: center;
            justify-content: center;
            background-color: #e8f5e9;
            font-size: 14px;
            font-weight: 500;
            color: #2e7d32;
          }

          .dropdown-menu-clean {
            position: absolute;
            top: 100%;
            right: 0;
            background: #ffffff;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            padding: 0.5rem 0;
            z-index: 1050;
            min-width: 140px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            margin-top: 0.5rem;
          }

          .dropdown-item-clean {
            padding: 0.75rem 1rem;
            font-size: 14px;
            color: #333333;
            text-decoration: none;
            display: flex;
            align-items: center;
            transition: background-color 0.2s ease;
            cursor: pointer;
            border: none;
            background: none;
            width: 100%;
          }

          .dropdown-item-clean:hover {
            background-color: #f5f5f5;
          }

          .dropdown-item-clean svg {
            margin-right: 0.75rem;
            font-size: 16px;
          }

          .nav-end-section button,
          .nav-end-section a {
            white-space: nowrap;
          }

          .menu-toggler {
            background: none;
            border: none;
            font-size: 20px;
            cursor: pointer;
            color: #333333;
            padding: 0;
          }

          .menu-toggler:hover {
            color: #555555;
          }

          .admin-badge {
            font-size: 11px;
            color: #d32f2f;
            font-weight: 600;
            margin-left: 0.25rem;
          }

          .bulk-import-container {
            position: relative;
          }

          .bulk-import-button {
            font-size: 15px;
            font-weight: 400;
            color: #333333;
            background: none;
            border: none;
            cursor: pointer;
            transition: color 0.2s ease;
            padding: 0;
            display: flex;
            align-items: center;
            gap: 0.5rem;
            white-space: nowrap;
          }

          .bulk-import-button:hover {
            color: #555555;
          }

          .bulk-import-dropdown {
            position: absolute;
            top: 100%;
            left: 0;
            background: #ffffff;
            border: 1px solid #e0e0e0;
            border-radius: 4px;
            padding: 0.5rem 0;
            z-index: 1040;
            min-width: 180px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
            margin-top: 0.5rem;
          }

          @media (max-width: 991px) {
            .navbar-clean .container-fluid {
              flex-wrap: wrap;
            }

            .nav-center-items {
              flex-basis: 100%;
              flex-direction: column;
              gap: 1rem;
              margin: 1rem 0;
              border-top: 1px solid #e0e0e0;
              padding-top: 1rem;
              align-items: flex-start;
            }

            .nav-end-section {
              flex-basis: 100%;
              margin: 1rem 0 0 0;
              border-top: 1px solid #e0e0e0;
              padding-top: 1rem;
              justify-content: flex-start;
              gap: 1rem;
            }

            .button-nav {
              gap: 1rem;
            }
          }
        `}
      </style>

      <nav className="navbar-clean w-100">
        <div className="container-fluid">
          <div>
            <Sidebar logoutComponent={logoutComponent} />
          </div>

          <div className="logo-container">
            <Link to={user ? "/dashboard" : "/"}>
              <img className="logo-w" src={"/img/CFID-logo.png"} alt="logo" />
            </Link>
          </div>

          {user && (
            <div className="nav-center-items" style={{ display: isMobile && !isNavOpen ? "none" : "flex" }}>
              <Link
                to="/add-company"
                onClick={handleLinkClick}
                className={`nav-link-clean ${
                  location.pathname === "/add-company" ? "active" : ""
                }`}
              >
                Add Company
              </Link>

              <Link
                to="/my-company"
                onClick={handleLinkClick}
                className={`nav-link-clean ${
                  location.pathname === "/my-company" ? "active" : ""
                }`}
              >
                My Company
              </Link>

              {isAdmin && (
                <div className="bulk-import-container" ref={bulkImportRef}>
                  <button
                    className="bulk-import-button"
                    onClick={() => setIsBulkImportOpen(!isBulkImportOpen)}
                  >
                    Bulk Import
                    <span>▼</span>
                  </button>

                  {isBulkImportOpen && (
                    <div className="bulk-import-dropdown">
                      <button
                        className="dropdown-item-clean"
                        onClick={handleDownloadTemplate}
                        disabled={loading}
                      >
                        {loading ? "Downloading..." : "Download Template"}
                      </button>

                      <button
                        className="dropdown-item-clean"
                        onClick={handleUploadClick}
                        disabled={uploading}
                      >
                        {uploading ? "Uploading..." : "Upload Template"}
                      </button>
                    </div>
                  )}
                </div>
              )}

              <Link
                to="/contactus"
                onClick={handleLinkClick}
                className={`nav-link-clean ${
                  location.pathname === "/contactus" ? "active" : ""
                }`}
              >
                Contact Us
              </Link>

              {isMobile && <MenuButton handleLinkClick={handleLinkClick} />}
            </div>
          )}

          <input
            ref={fileInputRef}
            type="file"
            accept=".xlsx"
            style={{ display: "none" }}
            onChange={handleFileChange}
          />

          <div className="nav-end-section" style={{ display: isMobile && !isNavOpen ? "none" : "flex" }}>
            {!user ? (
              <>
                <Link
                  to="/contactus"
                  onClick={handleLinkClick}
                  className={`nav-link-clean ${
                    location.pathname === "/contactus" ? "active" : ""
                  }`}
                >
                  Contact Us
                </Link>
                <SignIn />
                <SignUp />
              </>
            ) : (
              <div ref={dropdownRef} style={{ position: "relative" }}>
                <button
                  className="profile-button"
                  onClick={() => setIsLoggedIn(!isLoggedIn)}
                >
                  {localStorage.getItem("user") ? (
                    <div className="profile-initial">
                      {loginuser?.[0]?.toUpperCase()}
                    </div>
                  ) : (
                    <img src={dummyImage} alt="Profile" />
                  )}
                </button>

                {isLoggedIn && (
                  <div className="dropdown-menu-clean">
                    <a
                      href="#"
                      className="dropdown-item-clean"
                      onClick={(e) => {
                        e.preventDefault();
                        logoutComponent();
                      }}
                    >
                      <LogoutIcon />
                      Logout
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </nav>
    </>
  );
}

export default Navbar;